import type { MasterProduct } from 'apis/master-products';
import { useMemo, useState } from 'react';
import type { EditableVariantRow } from './editable-variant-row.types';
import {
  appendRow,
  appendRowFrom,
  buildBulkPayload,
  duplicateRowAt,
  hasUnsavedChanges,
  insertRowAt,
  mapVariantToRow,
  prependRow,
  removeRowAt,
  removeRowByClientId,
  validateRows,
} from './row-helpers';

export function useEditableVariantsRows(initialVariants: MasterProduct[]) {
  // `original` and `rows` both start from the same seed, but from here on
  // neither is derived from `initialVariants` again automatically — the only
  // way either updates after mount is through the setters below or an
  // explicit `reset()` call. This replaces the old remount-on-refetch hack:
  // callers gate `reset` on `!dirty` for background refetches (so in-progress
  // edits are never silently discarded), and call it unconditionally right
  // after a successful save or an explicit Cancel.
  const [original, setOriginal] = useState<EditableVariantRow[]>(() => initialVariants.map(mapVariantToRow));
  const [rows, setRows] = useState<EditableVariantRow[]>(original);

  const updateRow = (clientId: string, patch: Partial<EditableVariantRow>) =>
    setRows((prev) => prev.map((r) => (r.clientId === clientId ? { ...r, ...patch } : r)));

  const insert = (index: number, position: 'above' | 'below') => setRows((prev) => insertRowAt(prev, index, position));
  const remove = (index: number) => setRows((prev) => removeRowAt(prev, index));
  // Removes a SAVED row (one already present in `original`, e.g. after a confirmed
  // server-side delete) by clientId from both `rows` and `original` so the two stay
  // length-matched and `dirty` returns to false instead of getting stuck true forever.
  // Do not reuse `remove(index)` for this — that only touches `rows` and is still
  // needed as-is for splicing out never-saved local rows.
  const removeSavedRow = (clientId: string) => {
    setRows((prev) => removeRowByClientId(prev, clientId));
    setOriginal((prev) => removeRowByClientId(prev, clientId));
  };
  const append = () => setRows((prev) => appendRow(prev));
  const prepend = () => setRows((prev) => prependRow(prev));
  const duplicate = (index: number) => setRows((prev) => duplicateRowAt(prev, index));
  const appendFrom = () => setRows((prev) => appendRowFrom(prev));

  const reset = (variants: MasterProduct[]) => {
    const next = variants.map(mapVariantToRow);
    setOriginal(next);
    setRows(next);
  };

  const errors = useMemo(() => validateRows(rows, original), [rows, original]);

  // Saved rows whose PACKAGING (UOM ladder) fields differ from their pristine
  // state — the packaging expander uses this to warn when the edit will
  // re-derive recorded supplier prices. String-field comparison is exact: the
  // row model keeps these as strings, and pristine rows come through the same
  // mapVariantToRow normalization.
  const packagingDirtyClientIds = useMemo(() => {
    const PACKAGING_KEYS = ['baseUnit', 'itemUnit', 'netContent', 'packUnit', 'packSize'] as const;
    const originalByClientId = new Map(original.map((row) => [row.clientId, row]));
    const dirtyIds = new Set<string>();
    for (const row of rows) {
      const pristine = originalByClientId.get(row.clientId);
      if (pristine && PACKAGING_KEYS.some((key) => pristine[key] !== row[key])) dirtyIds.add(row.clientId);
    }
    return dirtyIds;
  }, [rows, original]);
  const errorsByClientId = useMemo(() => new Map(errors.map((e) => [e.clientId, e.messages])), [errors]);
  const dirty = useMemo(() => hasUnsavedChanges(rows, original), [rows, original]);

  return {
    rows,
    errors,
    errorsByClientId,
    packagingDirtyClientIds,
    dirty,
    updateRow,
    insert,
    remove,
    removeSavedRow,
    append,
    prepend,
    duplicate,
    appendFrom,
    reset,
    buildPayload: () => buildBulkPayload(rows, original),
  };
}
